import { useMemo, useState } from 'react';
import type { Layer } from '@geosoft/viewer-sdk';
import { Chip, Icon, Stack, Text, tokens } from '@/design';
import { clusterPseudoAssets, pseudoAssets } from './mapModel';

interface MapClusterOverlayProps {
  layers: Layer[];
  onSelectLayer(layerId: string): void;
}

export function MapClusterOverlay({ layers, onSelectLayer }: MapClusterOverlayProps) {
  const [openClusterId, setOpenClusterId] = useState<string | null>(null);
  const clusters = useMemo(() => clusterPseudoAssets(pseudoAssets(layers)), [layers]);
  if (clusters.length === 0) return null;

  const selectedCluster = clusters.find((cluster) => cluster.id === openClusterId) ?? null;
  const displayClusters = clusters.filter((cluster) => cluster.count > 1).slice(0, 3);

  return (
    <div
      style={{
        position: 'absolute',
        bottom: tokens.space[2],
        left: tokens.space[2],
        zIndex: tokens.zIndex.panel,
        width: tokens.width.popoverMd,
      }}
    >
      <div
        style={{
          border: tokens.border.thin,
          borderRadius: tokens.radius.md,
          background: tokens.color.surface[1],
          padding: tokens.space[2],
        }}
      >
        <Stack direction="row" align="center" gap={1}>
          <Icon name="atr" size={20} decorative />
          <Text variant="body" as="span">
            Asset clusters
          </Text>
        </Stack>
        <Stack direction="row" gap={1} style={{ marginTop: tokens.space[2], flexWrap: 'wrap' }}>
          {displayClusters.map((cluster) => (
            <button
              key={cluster.id}
              type="button"
              onClick={() => setOpenClusterId(cluster.id)}
              style={{
                appearance: 'none',
                border: tokens.border.thin,
                borderRadius: tokens.radius.pill,
                background: tokens.color.surface[2],
                color: tokens.color.ink.primary,
                minHeight: tokens.density.touchMin,
                paddingInline: tokens.space[2],
                cursor: 'pointer',
              }}
            >
              <Stack direction="row" align="center" gap={1}>
                <Icon name="atr" size={20} decorative />
                <Chip icon="layers" label={cluster.count} variant="neutral" />
              </Stack>
            </button>
          ))}
          {displayClusters.length === 0 ? (
            <Chip
              icon="atr"
              label="No multi-asset clusters yet"
              variant="neutral"
              aria-label="No cluster aggregations currently available"
            />
          ) : null}
        </Stack>
        {selectedCluster ? (
          <div
            role="dialog"
            aria-label="Cluster detail"
            style={{
              marginTop: tokens.space[2],
              border: tokens.border.thin,
              borderRadius: tokens.radius.input,
              background: tokens.color.surface[2],
              padding: tokens.space[2],
            }}
          >
            <Stack direction="row" align="center" gap={1}>
              <Text variant="caption" as="span">
                {selectedCluster.count} assets in cluster
              </Text>
              <span style={{ flex: '1 1 auto' }} />
              <button
                type="button"
                onClick={() => setOpenClusterId(null)}
                style={{
                  appearance: 'none',
                  border: 0,
                  background: 'transparent',
                  color: tokens.color.ink.secondary,
                  cursor: 'pointer',
                }}
              >
                <Icon name="close" size={20} decorative />
              </button>
            </Stack>
            <Stack direction="column" gap={1} style={{ marginTop: tokens.space[1] }}>
              {selectedCluster.assets.map((asset) => (
                <button
                  key={asset.id}
                  type="button"
                  onClick={() => onSelectLayer(asset.layerId)}
                  style={{
                    appearance: 'none',
                    border: tokens.border.thin,
                    borderRadius: tokens.radius.input,
                    background: tokens.color.surface[1],
                    color: tokens.color.ink.primary,
                    textAlign: 'left',
                    minHeight: tokens.density.touchMin,
                    paddingInline: tokens.space[2],
                    cursor: 'pointer',
                  }}
                >
                  <Stack direction="row" align="center" gap={1}>
                    <Icon name="location_on" size={20} decorative />
                    <Text variant="body" as="span">
                      {asset.label}
                    </Text>
                  </Stack>
                </button>
              ))}
            </Stack>
          </div>
        ) : null}
      </div>
    </div>
  );
}
